import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Badge, Card, CardContent, Skeleton } from '@mlm/ui';
import { listMyOrders, type OrderRow } from '../../lib/api';
import { fmtCurrency } from '../../lib/format';

const PENDING_STATUSES = ['paid', 'to_pay', 'to_pick', 'to_ship', 'shipping', 'to_release'];

const STATUS_LABEL: Record<string, string> = {
  pending: 'Pending payment',
  to_pay: 'To pay',
  paid: 'Paid',
  to_pick: 'Preparing',
  to_ship: 'Ready to ship',
  shipping: 'In transit',
  to_release: 'At cashier',
};

function statusBadge(status: string) {
  const label = STATUS_LABEL[status] ?? status;
  const variant: 'default' | 'secondary' | 'outline' =
    status === 'shipping' || status === 'to_ship' ? 'default' : 'secondary';
  return <Badge variant={variant}>{label}</Badge>;
}

/**
 * Dashboard widget that surfaces a member's in-flight orders so they don't have
 * to dig into /orders to remember what's still moving. Cap at 3 — anything
 * longer belongs on the full orders page.
 */
export function PendingOrdersPanel({ slotId }: { slotId: string | null }) {
  const q = useQuery({
    queryKey: ['pending-orders', slotId],
    queryFn: () => listMyOrders(slotId ?? undefined, { statuses: PENDING_STATUSES, limit: 3 }),
  });

  if (q.isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <Skeleton className="h-24 w-full" />
        </CardContent>
      </Card>
    );
  }

  const orders: OrderRow[] = q.data ?? [];

  return (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <p className="text-caption font-medium uppercase tracking-[0.1em] text-muted-foreground">
            Pending orders
          </p>
          <Link to="/orders" className="text-caption text-muted-foreground hover:underline">
            View all
          </Link>
        </div>

        {orders.length === 0 ? (
          <div className="mt-3 space-y-2 text-sm text-muted-foreground">
            <p>No pending orders.</p>
            <Link to="/storefront" className="text-primary hover:underline">
              Browse the store →
            </Link>
          </div>
        ) : (
          <ul className="mt-3 space-y-2">
            {orders.map((o) => (
              <li key={o.id}>
                <Link
                  to={`/orders/${o.id}`}
                  className="group flex items-center justify-between gap-2 rounded-md px-2 py-2 hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium" title={o.code}>
                      {o.code}
                    </p>
                    <div className="mt-1">{statusBadge(o.status)}</div>
                  </div>
                  <p className="shrink-0 text-sm tabular-nums">{fmtCurrency(o.total)}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
